import React, { useState, useRef, useEffect } from "react";
import { Button } from "antd";
import {
  BoldOutlined,
  UnderlineOutlined,
  ItalicOutlined,
} from "@ant-design/icons";

const DeclineLoanModal = ({
  isDeclineLoanModalOpen,
  onClose,
  finishDeclineDisburse,
  setDeclineText,
  declineFile,
  setDeclineFile,
  isButtonDisabled,
}) => {
  const [file, setFile] = useState(null);
  const editableRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [activeStyles, setActiveStyles] = useState([]);

  useEffect(() => {
    const handleInput = () => {
      const text = editableRef.current.innerText.trim();
      setIsEmpty(!text);
      setDeclineText(text);
    };

    const div = editableRef.current;
    div.addEventListener("input", handleInput);
    return () => div.removeEventListener("input", handleInput);
  }, []);

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      setDeclineFile(uploadedFile);
    }
  };

  const applyStyle = (style) => {
    document.execCommand(style, false, null);
    updateActiveStyles();
  };

  const updateActiveStyles = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const parent = selection.getRangeAt(0).commonAncestorContainer.parentNode;
    const isBold = document.queryCommandState("bold");
    const isUnderline = document.queryCommandState("underline");
    const isItalic = document.queryCommandState("italic");

    const newActiveStyles = [];
    if (isBold) newActiveStyles.push("bold");
    if (isUnderline) newActiveStyles.push("underline");
    if (isItalic) newActiveStyles.push("italic");

    setActiveStyles(newActiveStyles);
  };

  const handleClose = () => {
    setFile(null);
    setDeclineFile(null);
    setDeclineText("");
    if (editableRef.current) {
      editableRef.current.innerText = "";
    }
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center ${
        isDeclineLoanModalOpen ? "block" : "hidden"
      } bg-black bg-opacity-50`}
    >
      <div className="relative bg-white shadow max-w-[60%] h-[66vh] mx-auto p-6 rounded-sm">
        <h1 className="text-lg font-bold">Decline Loan Disbursement</h1>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-xl"
        >
          ✖
        </button>
        <p className="text-sm text-[#000000] mt-4">
          Provide details for declining this loan disbursement. Clearly state
          the reasons for the decision and include any
        </p>
        <p className="text-sm text-[#000000]">
          necessary notes for record-keeping. This information may be used for
          future reference and borrower
        </p>
        <p className="text-sm text-[#000000]">communication.</p>

        {/* Text Editor */}
        <div className="border border-[#000000] rounded-md h-[234px] p-3 mt-4 relative">
          <div
            ref={editableRef}
            contentEditable
            className="w-full p-2 h-[148px] min-h-[100px] max-h-[148px] overflow-auto outline-none break-words"
            onKeyUp={updateActiveStyles}
            onMouseUp={updateActiveStyles}
          />
          {isEmpty && (
            <p className="absolute left-3 top-5 text-gray-400 pointer-events-none">
              Type here*
            </p>
          )}

          {/* Formatting Buttons */}
          <div className="gap-2 border-t p-2 rounded-md flex items-center cursor-pointer w-full absolute bottom-0 left-0">
            <Button
              icon={<BoldOutlined />}
              onClick={() => applyStyle("bold")}
              className={
                activeStyles.includes("bold") ? "border border-red-500" : ""
              }
            />
            <Button
              icon={<UnderlineOutlined />}
              onClick={() => applyStyle("underline")}
              className={
                activeStyles.includes("underline")
                  ? "border border-red-500"
                  : ""
              }
            />
            <Button
              icon={<ItalicOutlined />}
              onClick={() => applyStyle("italic")}
              className={
                activeStyles.includes("italic") ? "border border-red-500" : ""
              }
            />
          </div>
        </div>

        {/* File Upload */}
        <label className="border p-2 rounded-md flex items-center gap-2 cursor-pointer mt-4 w-[340px]">
          <img src="/attach-file.png" alt="attach-file" className="w-5 h-5" />
          <span className="text-sm">
            Attach a file (.pdf, .docx, .doc, .jpeg, .png)
          </span>
          <input
            key={file ? file.name : "file-upload"}
            type="file"
            accept=".pdf,.docx,.doc,.jpeg,.png"
            className="hidden"
            onChange={handleFileChange}
          />
        </label>

        {/* File Preview */}
        {file && (
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
            <button onClick={() => setFile(null)} className="text-lg">
              <img src="/grey-cancel.png" alt="cancel" />
            </button>
            {declineFile && <p>{declineFile.name}</p>}
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={finishDeclineDisburse}
          disabled={isButtonDisabled}
          className={`w-[230px] h-[51px] py-2 rounded-md mt-[65px] ml-auto block ${
            isButtonDisabled
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#074073] text-white"
          }`}
        >
          Finish
        </button>
      </div>
    </div>
  );
};

export default DeclineLoanModal;
